import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  Divider,
  Chip,
  Collapse,
  ButtonBase,
} from '@mui/material';
import { Add, Edit, Delete, RemoveShoppingCart, ExpandMore } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import dayjs from 'dayjs';
import api from '../api';
import LoadingState from './LoadingState';
import { useSnackbar } from 'notistack';
import PageHeader from './PageHeader';
import { safeSessionStorage } from '../utils/safeStorage';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
} from 'recharts';

const defaultForm = {
  person: '',
  itemName: '',
  price: '',
  quantity: 1,
  purchaseDate: dayjs().format('YYYY-MM-DD'),
  notes: '',
  eventId: '',
};

const chartColors = ['#E8891A', '#2D6A4F', '#52B788', '#C46F0C', '#E11D48', '#7C3AED'];

function Purchases() {
  const [purchases, setPurchases] = useState([]);
  const [events, setEvents] = useState([]);
  const [filterEvent, setFilterEvent] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState(defaultForm);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const itemFieldRef = useRef(null);
  const personFieldRef = useRef(null);
  const [peopleOptions, setPeopleOptions] = useState([]);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [priceDraft, setPriceDraft] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [personError, setPersonError] = useState('');
  const [itemError, setItemError] = useState('');
  const [eventError, setEventError] = useState('');
  const [dateError, setDateError] = useState('');
  const [personOpen, setPersonOpen] = useState(false);
  const [showCharts, setShowCharts] = useState(() =>
    safeSessionStorage.getJSON('purchases_showCharts', true)
  );

  // People whose purchase sections are collapsed; remembered for the session.
  const [collapsedPeople, setCollapsedPeople] = useState(() => {
    const saved = safeSessionStorage.getJSON('purchases_collapsedPeople', []);
    return Array.isArray(saved) ? saved : [];
  });

  const togglePersonCollapsed = (person) => {
    setCollapsedPeople((prev) => {
      const next = prev.includes(person) ? prev.filter((p) => p !== person) : [...prev, person];
      safeSessionStorage.setJSON('purchases_collapsedPeople', next);
      return next;
    });
  };

  const loadPurchases = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/purchases');
      setPurchases(response.data || []);
    } catch (err) {
      console.error('Failed to fetch purchases', err);
      setError('Failed to load purchases.');
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      const response = await api.get('/events');
      const eventData = response.data || [];
      setEvents(eventData);
      const peopleMap = new Map();
      eventData.forEach((event) => {
        if (Array.isArray(event.members)) {
          event.members.forEach((member) => {
            const name = member.name?.trim();
            const label = name || member.email;
            if (label && !peopleMap.has(label)) {
              peopleMap.set(label, {
                label,
                secondary: name && member.email ? member.email : '',
              });
            }
          });
        }
      });
      const options = Array.from(peopleMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label)
      );
      setPeopleOptions(options);
    } catch (err) {
      console.error('Failed to fetch events', err);
    }
  };

  const location = useLocation();

  useEffect(() => {
    loadPurchases();
    loadEvents();
  }, []);

  // Check if we should auto-open the dialog based on navigation state
  useEffect(() => {
    if (location.state?.openDialog) {
      handleOpenDialog();
      // Clear the state so it doesn't reopen on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleOpenDialog = (purchase = null) => {
    if (purchase) {
      setEditingPurchase(purchase);
      setFormState({
        person: purchase.person,
        itemName: purchase.itemName,
        price: purchase.price ?? '',
        quantity: purchase.quantity || 1,
        purchaseDate: purchase.purchaseDate
          ? dayjs(purchase.purchaseDate).format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD'),
        notes: purchase.notes || '',
        eventId: purchase.event || '',
      });
      setShowOptionalFields(true);
    } else {
      setEditingPurchase(null);
      setFormState(defaultForm);
      setShowOptionalFields(false);
      setPersonError('');
      setItemError('');
      setEventError('');
      setDateError('');
    }
    setDialogOpen(true);
    setTimeout(() => {
      personFieldRef.current?.focus();
    }, 0);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPurchase(null);
    setShowOptionalFields(false);
    setPersonError('');
    setItemError('');
    setEventError('');
    setDateError('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => {
      const newState = { ...prev, [name]: value };

      // Auto-set date if event is selected
      if (name === 'eventId') {
        const selectedEvent = events.find(ev => ev._id === value);
        if (selectedEvent && selectedEvent.date) {
          newState.purchaseDate = dayjs(selectedEvent.date).format('YYYY-MM-DD');
        }
      }

      return newState;
    });

    if (name === 'person') {
      setPersonError('');
    }
    if (name === 'itemName') {
      setItemError('');
    }
    if (name === 'eventId') {
      setEventError('');
    }
    if (name === 'purchaseDate') {
      setDateError('');
    }
  };

  const handleSave = async ({ addAnother = false } = {}) => {
    const missingPerson = !formState.person.trim();
    const missingItem = !formState.itemName.trim();
    const missingEvent = !formState.eventId;
    const missingDate = !formState.purchaseDate;

    setPersonError(missingPerson ? 'Person is required.' : '');
    setItemError(missingItem ? 'Item is required.' : '');
    setEventError(missingEvent ? 'Event is required.' : '');
    setDateError(missingDate ? 'Date is required.' : '');

    if (missingPerson || missingItem || missingEvent || missingDate) {
      return;
    }

    const payload = {
      ...formState,
      price: formState.price === '' ? '' : Number(formState.price),
      quantity: Number(formState.quantity) || 1,
      eventId: formState.eventId || undefined,
    };

    try {
      if (editingPurchase) {
        await api.put(`/purchases/${editingPurchase._id}`, payload);
      } else {
        await api.post('/purchases', payload);
      }
      await loadPurchases();
      if (addAnother && !editingPurchase) {
        setFormState((prev) => ({
          ...prev,
          itemName: '',
          price: '',
          quantity: 1,
        }));
        setTimeout(() => {
          itemFieldRef.current?.focus();
        }, 0);
      } else {
        handleCloseDialog();
      }
    } catch (err) {
      console.error('Failed to save purchase', err);
      const message = err?.response?.data?.message || 'Failed to save purchase.';
      setPersonError(message);
    }
  };

  const handleDelete = async (purchaseId) => {
    try {
      await api.delete(`/purchases/${purchaseId}`);
      setPurchases((prev) => prev.filter((purchase) => purchase._id !== purchaseId));
      enqueueSnackbar('Purchase removed.', { variant: 'info' });
    } catch (err) {
      console.error('Failed to delete purchase', err);
      setError('Failed to delete purchase.');
    }
  };

  const handleInlinePriceSave = async () => {
    if (!editingPriceId) return;
    try {
      await api.put(`/purchases/${editingPriceId}`, {
        price: priceDraft === '' ? '' : Number(priceDraft),
      });
      setPurchases((prev) =>
        prev.map((purchase) =>
          purchase._id === editingPriceId
            ? {
              ...purchase,
              price: priceDraft === '' ? null : Number(priceDraft),
            }
            : purchase
        )
      );
    } catch (err) {
      console.error('Failed to update price', err);
      enqueueSnackbar('Unable to update price.', { variant: 'error' });
    } finally {
      setEditingPriceId(null);
      setPriceDraft('');
    }
  };

  const handleUndoLinkedPurchase = async (purchase) => {
    if (!purchase.sourceListId || !purchase.sourceItemId) {
      return;
    }
    try {
      await api.post(`/lists/${purchase.sourceListId}/items/${purchase.sourceItemId}/unpurchase`);
      await api.delete(`/purchases/${purchase._id}`);
      enqueueSnackbar('Purchase undone.', { variant: 'success' });
      setPurchases((prev) => prev.filter((item) => item._id !== purchase._id));
    } catch (err) {
      console.error('Failed to undo purchase', err);
      enqueueSnackbar('Failed to undo purchase.', { variant: 'error' });
    }
  };

  const toggleCharts = () => {
    setShowCharts((prev) => {
      const newValue = !prev;
      safeSessionStorage.setJSON('purchases_showCharts', newValue);
      return newValue;
    });
  };

  const filteredPurchases = useMemo(() => {
    if (filterEvent === 'all') {
      return purchases;
    }
    if (filterEvent === 'unevented') {
      return purchases.filter((purchase) => !purchase.event);
    }
    return purchases.filter((purchase) => purchase.event === filterEvent);
  }, [purchases, filterEvent]);

  const groupedByPerson = useMemo(() => {
    return filteredPurchases.reduce((acc, purchase) => {
      const key = purchase.person || 'Someone';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(purchase);
      return acc;
    }, {});
  }, [filteredPurchases]);

  const totalSpent = useMemo(() => {
    return filteredPurchases.reduce((sum, purchase) => {
      const price = Number(purchase.price) || 0;
      const quantity = Number(purchase.quantity) || 1;
      return sum + price * quantity;
    }, 0);
  }, [filteredPurchases]);

  const eventTotals = useMemo(() => {
    return purchases.reduce((acc, purchase) => {
      const label = purchase.eventName || 'Unevented';
      const price = Number(purchase.price) || 0;
      const quantity = Number(purchase.quantity) || 1;
      acc[label] = (acc[label] || 0) + price * quantity;
      return acc;
    }, {});
  }, [purchases]);

  const eventsWithPurchaseIds = useMemo(() => {
    const ids = new Set();
    purchases.forEach((purchase) => {
      if (purchase.event) {
        ids.add(purchase.event);
      }
    });
    return ids;
  }, [purchases]);

  const hasUneventedPurchases = useMemo(
    () => purchases.some((purchase) => !purchase.event),
    [purchases]
  );

  const eventsWithPurchases = useMemo(() => {
    const list = events.filter((event) => eventsWithPurchaseIds.has(event._id));
    if (hasUneventedPurchases) {
      list.push({ _id: 'unevented', name: 'Unevented' });
    }
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [events, eventsWithPurchaseIds, hasUneventedPurchases]);

  const eventsWithoutPurchases = useMemo(() => {
    const list = events.filter((event) => !eventsWithPurchaseIds.has(event._id));
    if (!hasUneventedPurchases) {
      list.push({ _id: 'unevented', name: 'Unevented' });
    }
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [events, eventsWithPurchaseIds, hasUneventedPurchases]);

  const filteredEventTotals = useMemo(() => {
    return filteredPurchases.reduce((acc, purchase) => {
      const label = purchase.eventName || 'Unevented';
      const price = Number(purchase.price) || 0;
      const quantity = Number(purchase.quantity) || 1;
      acc[label] = (acc[label] || 0) + price * quantity;
      return acc;
    }, {});
  }, [filteredPurchases]);

  const spendByPerson = useMemo(() => {
    return Object.entries(groupedByPerson).map(([name, items]) => {
      const total = items.reduce((sum, purchase) => {
        const price = Number(purchase.price) || 0;
        const quantity = Number(purchase.quantity) || 1;
        return sum + price * quantity;
      }, 0);
      return { name, total };
    });
  }, [groupedByPerson]);

  const trendData = useMemo(() => {
    const buckets = filteredPurchases.reduce((acc, purchase) => {
      if (!purchase.purchaseDate) {
        return acc;
      }
      const key = dayjs(purchase.purchaseDate).format('YYYY-MM');
      const price = Number(purchase.price) || 0;
      const quantity = Number(purchase.quantity) || 1;
      acc[key] = (acc[key] || 0) + price * quantity;
      return acc;
    }, {});
    return Object.entries(buckets)
      .map(([month, value]) => ({ month, value }))
      .sort((a, b) => (a.month > b.month ? 1 : -1));
  }, [filteredPurchases]);

  const renderNoData = (label) => (
    <Box display="flex" alignItems="center" justifyContent="center" height={220}>
      <Typography color="text.secondary" variant="body2">
        No {label} data yet.
      </Typography>
    </Box>
  );

  if (loading) {
    return <LoadingState message="Loading purchases..." />;
  }

  return (
    <Box>
      <PageHeader
        title="My Purchases"
        variant="h4"
        mb={3}
        action={
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              select
              label="Filter by event"
              size="small"
              value={filterEvent}
              onChange={(e) => setFilterEvent(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">All events</MenuItem>
              {eventsWithPurchases.map((event) => (
                <MenuItem
                  key={event._id}
                  value={event._id}
                >
                  {event.name}
                </MenuItem>
              ))}
              {eventsWithoutPurchases.map((event) => (
                <MenuItem
                  key={event._id}
                  value={event._id}
                  sx={{ color: 'text.disabled' }}
                >
                  {event.name}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
              Add Purchase
            </Button>
          </Box>
        }
      />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            TOTAL SPENT
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 700 }}
          >
            ${totalSpent.toFixed(2)}
          </Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={toggleCharts}>
          {showCharts ? 'Hide Charts' : 'Show Charts'}
        </Button>
      </Box>

      {error && (
        <Box mb={2}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <Box mb={2} display="flex" gap={1} flexWrap="wrap">
        {Object.entries(eventTotals).map(([label, value], index) => (
          <Chip
            key={label}
            label={`${label}: $${value.toFixed(2)}`}
            variant="outlined"
            sx={{
              borderColor: chartColors[index % chartColors.length],
              color: chartColors[index % chartColors.length],
              backgroundColor: `${chartColors[index % chartColors.length]}12`,
            }}
          />
        ))}
      </Box>

      <Collapse in={showCharts}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="overline" color="text.secondary">
                MONTHLY TREND
              </Typography>
              {trendData.length === 0 ? (
                renderNoData('trend')
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                    <Line type="monotone" dataKey="value" stroke={chartColors[0]} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="overline" color="text.secondary">
                SPEND BY EVENT
              </Typography>
              {Object.keys(filteredEventTotals).length === 0 ? (
                renderNoData('event')
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={Object.entries(filteredEventTotals).map(([name, value]) => ({
                        name,
                        value,
                      }))}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={80}
                      label
                    >
                      {Object.entries(filteredEventTotals).map((entry, index) => (
                        <Cell
                          key={entry[0]}
                          fill={chartColors[index % chartColors.length]}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="overline" color="text.secondary">
                SPEND BY PERSON
              </Typography>
              {spendByPerson.length === 0 ? (
                renderNoData('person')
              ) : (
                <Box sx={{ width: '100%', height: Math.max(300, spendByPerson.length * 60) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={spendByPerson}
                      margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={120} />
                      <RechartsTooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                      <Bar dataKey="total" fill={chartColors[0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Collapse>

      {Object.keys(groupedByPerson).length === 0 ? (
        <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography>No purchases tracked yet.</Typography>
        </Paper>
      ) : (
        Object.entries(groupedByPerson).map(([person, items]) => {
          const subtotal = items.reduce((sum, purchase) => {
            const price = Number(purchase.price) || 0;
            const quantity = Number(purchase.quantity) || 1;
            return sum + price * quantity;
          }, 0);
          const isCollapsed = collapsedPeople.includes(person);
          const panelId = `purchases-${person.replace(/\W+/g, '-')}`;
          return (
            <Paper key={person} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
              <ButtonBase
                onClick={() => togglePersonCollapsed(person)}
                aria-expanded={!isCollapsed}
                aria-controls={panelId}
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  textAlign: 'left',
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                  <ExpandMore
                    sx={{
                      transition: 'transform 0.2s',
                      transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                    }}
                  />
                  <Typography variant="h6" component="span">
                    {person}
                  </Typography>
                  {isCollapsed && (
                    <Typography variant="body2" component="span" color="text.secondary">
                      {items.length} {items.length === 1 ? 'item' : 'items'}
                    </Typography>
                  )}
                </Box>
                <Typography variant="subtitle1" component="span" color="text.secondary" sx={{ pl: 1 }}>
                  Subtotal: ${subtotal.toFixed(2)}
                </Typography>
              </ButtonBase>
              <Collapse in={!isCollapsed} unmountOnExit id={panelId}>
              <Divider sx={{ mb: 2 }} />
              {items.map((purchase) => {
                const isLinkedPurchase = Boolean(purchase.sourceItemId && purchase.sourceListId);
                const isEditingPrice = editingPriceId === purchase._id;
                return (
                  <Box
                    key={purchase._id}
                    display="flex"
                    flexDirection={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    gap={1}
                    sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                  >
                    <Box>
                      <Typography variant="subtitle1">{purchase.itemName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {purchase.eventName ? `${purchase.eventName} • ` : ''}
                        {purchase.purchaseDate
                          ? dayjs(purchase.purchaseDate).format('MMM D, YYYY')
                          : 'Date unknown'}
                      </Typography>
                      {purchase.notes && (
                        <Typography variant="body2" color="text.secondary">
                          Notes: {purchase.notes}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      {isEditingPrice ? (
                        <TextField
                          size="small"
                          type="number"
                          value={priceDraft}
                          onChange={(e) => setPriceDraft(e.target.value)}
                          onBlur={handleInlinePriceSave}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleInlinePriceSave();
                            }
                            if (e.key === 'Escape') {
                              setEditingPriceId(null);
                              setPriceDraft('');
                            }
                          }}
                          inputProps={{ min: 0, step: '0.01' }}
                        />
                      ) : (
                        <Typography
                          variant="subtitle1"
                          onDoubleClick={() => {
                            setEditingPriceId(purchase._id);
                            setPriceDraft(
                              purchase.price === null || purchase.price === undefined
                                ? ''
                                : String(purchase.price)
                            );
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          {purchase.price !== null && purchase.price !== undefined
                            ? `$${purchase.price.toFixed(2)}`
                            : 'No price'}
                          {purchase.quantity > 1 ? ` × ${purchase.quantity}` : ''}
                        </Typography>
                      )}
                      <IconButton size="small" onClick={() => handleOpenDialog(purchase)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      {isLinkedPurchase ? (
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleUndoLinkedPurchase(purchase)}
                        >
                          <RemoveShoppingCart fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(purchase._id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              })}
              </Collapse>
            </Paper>
          );
        })
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault();
            handleSave({ addAnother: true });
          } else if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSave();
          }
        }}
      >
        <DialogTitle>{editingPurchase ? 'Edit Purchase' : 'Add Purchase'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={peopleOptions}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                value={formState.person}
                onChange={(event, newValue) =>
                  setFormState((prev) => ({
                    ...prev,
                    person:
                      typeof newValue === 'string'
                        ? newValue
                        : newValue?.label || '',
                  }))
                }
                inputValue={formState.person}
                onInputChange={(event, newInputValue) =>
                  setFormState((prev) => ({ ...prev, person: newInputValue || '' }))
                }
                onOpen={() => setPersonOpen(true)}
                onClose={() => setPersonOpen(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Tab' && personOpen && peopleOptions.length > 0) {
                    const normalized = (formState.person || '').toLowerCase().trim();
                    const filtered = normalized
                      ? peopleOptions.filter((opt) =>
                        opt.label.toLowerCase().includes(normalized)
                      )
                      : peopleOptions;
                    const first = filtered[0] || peopleOptions[0];
                    if (first) {
                      setFormState((prev) => ({
                        ...prev,
                        person: first.label,
                      }));
                    }
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box display="flex" flexDirection="column">
                      <Typography>{option.label}</Typography>
                      {option.secondary && (
                        <Typography variant="caption" color="text.secondary">
                          {option.secondary}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Person"
                    required
                    fullWidth
                    inputRef={personFieldRef}
                    error={Boolean(personError)}
                    helperText={personError || ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Item"
                name="itemName"
                value={formState.itemName}
                onChange={handleFormChange}
                fullWidth
                required
                inputRef={itemFieldRef}
                multiline
                minRows={2}
                error={Boolean(itemError)}
                helperText={itemError || ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Price"
                name="price"
                type="number"
                value={formState.price}
                onChange={handleFormChange}
                fullWidth
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Event"
                name="eventId"
                value={formState.eventId}
                onChange={handleFormChange}
                fullWidth
                required
                error={Boolean(eventError)}
                helperText={eventError || ''}
              >
                <MenuItem value="">
                  <em>Select an event</em>
                </MenuItem>
                {events.map((event) => (
                  <MenuItem key={event._id} value={event._id}>
                    {event.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date"
                name="purchaseDate"
                type="date"
                value={formState.purchaseDate}
                onChange={handleFormChange}
                fullWidth
                required
                error={Boolean(dateError)}
                helperText={dateError || ''}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Box mt={2}>
            <Button
              size="small"
              onClick={() => setShowOptionalFields((prev) => !prev)}
            >
              {showOptionalFields ? 'Hide optional details' : 'Show optional details'}
            </Button>
          </Box>

          <Collapse in={showOptionalFields}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Quantity (optional)"
                  name="quantity"
                  type="number"
                  value={formState.quantity}
                  onChange={handleFormChange}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes (optional)"
                  name="notes"
                  value={formState.notes}
                  onChange={handleFormChange}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>
          </Collapse>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {!editingPurchase && (
            <Button onClick={() => handleSave({ addAnother: true })} variant="outlined">
              Save & Add Another
            </Button>
          )}
          <Button onClick={() => handleSave()} variant="contained">
            {editingPurchase ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Purchases;
